import { MButton } from '@assets/mui/@material-extend';
import { useBitrix } from '@context/useBitrixUser/useBitrix';
import { zodResolver } from '@hookform/resolvers/zod';
import Typography from '@mui/material/Typography';
import { companyProfileService } from '@services/requests/companyProfileService';
import { MainData } from '@services/requests/companyProfileService/types/mainData';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ChangeNotification } from '../ChangeNotification';
import { CompanyModule } from '../CompanyProfileModules/CompanyModule';
import { EdoModule } from '../CompanyProfileModules/EdoModule';
import { RepresentativeModule } from '../CompanyProfileModules/RepresentativeModule';
import { RequiredDocumnetsModule } from '../CompanyProfileModules/RequiredDocumentsModule';
import {
  companyProfileExtendedFormSchema,
  companyProfileFormSchema,
  getDynamicCompanyProfileFormSchema,
} from './schemas';
import { CompanyProfileExtendedFormFields, CompanyProfileFormFields } from './types';

interface CompanyProfileModulesWrapperProps {
  mainData: MainData;
  isInspection: boolean;
  isAllowEdit: boolean;
  onChangeInspection: (value: boolean) => void;
}

function CompanyProfileModulesWrapper({
  mainData,
  isInspection,
  isAllowEdit,
  onChangeInspection,
}: CompanyProfileModulesWrapperProps) {
  const { fields: commonFields } = mainData.sections.common;
  const { fields: representativesFields } = mainData.sections.representatives;
  const { fields: edoFields } = mainData.sections.edo;

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isNeedReviewMessage, setIsNeedReviewMessage] = useState<boolean>(false);
  const [isChangeNotificationOpen, setIsChangeNotificationOpen] = useState<boolean>(false);

  const { useBitrixUser } = useBitrix();
  const [{ bitrixUser }] = useBitrixUser().useState();

  const defaultCompanyProfileRef = useRef<
    CompanyProfileFormFields & CompanyProfileExtendedFormFields
  >({
    fullName: commonFields.full_name.value ?? '',
    shortName: commonFields.short_name.value ?? '',
    firstName: commonFields.first_name.value ?? '',
    lastName: commonFields.last_name.value ?? '',
    secondName: commonFields.second_name.value ?? '',
    kpp: commonFields.kpp.value ?? '',
    legalAddress: commonFields.legal_address.value ?? '',
    postalAddress: commonFields.postal_address.value ?? '',
    settlementsCurrency: commonFields.currency.value,
    representativeFullName: representativesFields.director.value ?? '',
    jobTitle: representativesFields.job_position.value,
    actionReason: representativesFields.basis.value ?? '',
    availabilityEdo: edoFields.edo.value,
    operatorEdo: edoFields.edo_operator.value ?? '',
  });

  const form = useForm<CompanyProfileFormFields & CompanyProfileExtendedFormFields>({
    resolver: (data, context, options) => {
      if (mainData.sections.common.company_type === '2') {
        return zodResolver(
          getDynamicCompanyProfileFormSchema(
            companyProfileExtendedFormSchema,
            defaultCompanyProfileRef.current
          )
        )(data, context, options);
      }
      return zodResolver(
        getDynamicCompanyProfileFormSchema(
          companyProfileFormSchema,
          defaultCompanyProfileRef.current
        )
      )(data, context, options);
    },
    defaultValues: defaultCompanyProfileRef.current,
  });

  const { handleSubmit, reset, watch, getValues } = form;

  const currentCompanyProfileFormValues = watch();
  const needReview = Object.entries(currentCompanyProfileFormValues)
    .map(([key, value]) => (defaultCompanyProfileRef.current[key] !== value ? key : null))
    .filter((key) => key !== null)
    .some((key) => !['postalAddress', 'availabilityEdo', 'operatorEdo'].includes(key));

  const submitCompanyProfileFormHandler = async (
    data: CompanyProfileFormFields & CompanyProfileExtendedFormFields
  ) => {
    const result = await companyProfileService().SaveCompany(
      bitrixUser.id,
      mainData.sections.common.company_type,
      data
    );

    if (!result.success) {
      return;
    }

    const newDefaultValues: Record<string, unknown> = {};

    Object.entries(getValues()).forEach(([key, value]) => {
      if (value !== null && typeof value === 'object') {
        return;
      }
      newDefaultValues[key] = value;
    });

    defaultCompanyProfileRef.current = newDefaultValues;
    reset({ ...newDefaultValues });
    setIsChangeNotificationOpen(true);
    setIsEditMode(false);
    onChangeInspection(result.inspection);
    setIsNeedReviewMessage(needReview);
  };

  const cancelEditingHandler = () => {
    reset();
    setIsEditMode(false);
  };

  const openEditingHandler = () => {
    if (isInspection) {
      return;
    }

    setIsEditMode(true);
  };

  return (
    <>
      <form className="modules-form" onSubmit={handleSubmit(submitCompanyProfileFormHandler)}>
        <CompanyModule isEditMode={isEditMode} form={form} mainData={mainData} />
        <RepresentativeModule isEditMode={isEditMode} form={form} mainData={mainData} />
        <EdoModule isEditMode={isEditMode} form={form} mainData={mainData} />
        <RequiredDocumnetsModule
          defaultFormState={defaultCompanyProfileRef.current}
          form={form}
          mainData={mainData}
          isInspection={isInspection}
        />
        <div>
          {!isEditMode && isAllowEdit && (
            <MButton
              className={'main-tab-button'}
              variant={'outlined'}
              color={'secondary'}
              type="button"
              disabled={isInspection}
              onClick={openEditingHandler}
            >
              <Typography
                color={'#3460B4'}
                fontSize={16}
                fontWeight={600}
                sx={{ opacity: isInspection ? '0.5' : '1' }}
              >
                {mainData.messages.btn_company_info}
              </Typography>
            </MButton>
          )}
          {isEditMode && (
            <>
              <MButton
                className={'main-tab-button'}
                variant={needReview ? 'outlined' : 'contained'}
                color={'secondary'}
                type="submit"
                sx={{ marginRight: '20px' }}
              >
                <Typography
                  color={needReview ? '#3460B4' : '#ffffff'}
                  fontSize={16}
                  fontWeight={600}
                >
                  {needReview
                    ? mainData.messages.btn_send_for_inspection
                    : mainData.messages.btn_save}
                </Typography>
              </MButton>
              <MButton
                className={'main-tab-button'}
                variant={'outlined'}
                color={'secondary'}
                type="button"
                onClick={cancelEditingHandler}
              >
                <Typography color={'#3460B4'} fontSize={16} fontWeight={600}>
                  {mainData.messages.btn_undo}
                </Typography>
              </MButton>
            </>
          )}
        </div>
      </form>

      <ChangeNotification
        needReviewType={isNeedReviewMessage}
        isOpen={isChangeNotificationOpen}
        onClose={() => setIsChangeNotificationOpen(false)}
      />
    </>
  );
}

export { CompanyProfileModulesWrapper };
